import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  LinearProgress,
} from "@mui/material";
import { FileUploadButton } from "@/components/common";
import RichTextEditor from "@/components/common/RichTextEditor";
import { toast } from "react-toastify";
import {
  useCreateVocabularyWordMutation,
  useUpdateVocabularyWordMutation,
  useDeleteVocabularyWordMutation,
} from "@/store/api/admin/exerciseApi";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";

const WordFormDialog = ({
  open,
  onClose,
  editingWord,
  onSubmit,
  isLoading,
  dayId,
}) => {
  const [wordFormData, setWordFormData] = useState({
    word: "",
    pronunciation: "",
    translation: "",
    explanation: "",
    example: "",
    audio: null,
    image: null,
  });
  const [updateWord, { isLoading: isUpdatingWord }] =
    useUpdateVocabularyWordMutation();
  const [createWord, { isLoading: isCreatingWord }] =
    useCreateVocabularyWordMutation();
    const [isUploading, setIsUploading] = useState(false);

  // Update form when editingWord changes
  useEffect(() => {
    if (editingWord) {
      setWordFormData({
        word: editingWord.word || "",
        pronunciation: editingWord.pronunciation || "",
        translation: editingWord.translation || "",
        explanation: editingWord.explanation || "",
        example: editingWord.example || "",
        audio: null,
        image: null,
      });
    } else {
      setWordFormData({
        word: "",
        pronunciation: "",
        translation: "",
        explanation: "",
        example: "",
        audio: null,
        image: null,
      });
    }
  }, [editingWord, open]);

  const handleImageChange = (fileObj) => {
    const { file } = fileObj;
    setWordFormData((prev) => ({ ...prev, image: file }));
  };

  const handleAudioChange = (fileObj) => {
    const { file } = fileObj;
    setWordFormData((prev) => ({ ...prev, audio: file }));
  };

  // const handleSubmit = () => {
  //   onSubmit(formData);
  // };

  const handleSubmit = async () => {
    if (!wordFormData.word.trim()) {
      toast.error("Word is required");
      return;
    }

    if (!wordFormData.translation.trim()) {
      toast.error("Translation is required");
      return;
    }

    try {
      // Upload media files directly to Cloudinary
      const mediaFiles = {
        image: wordFormData.image,
        audio: wordFormData.audio,
      };
      setIsUploading(true);
      const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/vocabulary/${dayId}`);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("word", wordFormData.word);
      formData.append("translation", wordFormData.translation);
      if (wordFormData.pronunciation) {
        formData.append("pronunciation", wordFormData.pronunciation);
      }
      formData.append("explanation", wordFormData.explanation || "");
      if (wordFormData.example) {
        formData.append("example", wordFormData.example);
      }
      
      // Append uploaded URLs or existing URLs
      if (uploadedUrls.audioUrl) {
        formData.append("audioUrl", uploadedUrls.audioUrl);
      } else if (editingWord?.audioUrl) {
        formData.append("audioUrl", editingWord.audioUrl);
      }
      
      if (uploadedUrls.imageUrl) {
        formData.append("imageUrl", uploadedUrls.imageUrl);
      } else if (editingWord?.imageUrl) {
        formData.append("imageUrl", editingWord.imageUrl);
      }

      if (editingWord) {
        await updateWord({
          dayId,
          wordId: editingWord._id,
          wordData: formData,
        }).unwrap();
        toast.success("Word updated successfully");
      } else {
        await createWord({
          dayId,
          wordData: formData,
        }).unwrap();
        toast.success("Word added successfully");
      }
      setIsUploading(false);
      // setWordDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Failed to save word:", error);
      toast.error(error?.data?.message || "Failed to save word");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {(isLoading || isCreatingWord || isUpdatingWord || isUploading) && (  
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="indeterminate"
            color="primary"
            sx={{ mt: 0, height: 5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            {wordFormData.audio || wordFormData.image 
              ? 'Uploading images and saving question...' 
              : 'Saving question...'}
          </Typography>
        </Box>
      )}
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
        >
          {editingWord ? "Edit Word" : "Add New Word"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6">Basic Information</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Word"
              value={wordFormData.word}
              onChange={(e) =>
                setWordFormData((prev) => ({ ...prev, word: e.target.value }))
              }
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pronunciation"
              value={wordFormData.pronunciation}
              onChange={(e) =>
                setWordFormData((prev) => ({
                  ...prev,
                  pronunciation: e.target.value,
                }))
              }
              placeholder="e.g., /prəˌnʌnsiˈeɪʃən/"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Translation"
              value={wordFormData.translation}
              onChange={(e) =>
                setWordFormData((prev) => ({
                  ...prev,
                  translation: e.target.value,
                }))
              }
              required
              placeholder="Vietnamese translation"
            />
          </Grid>

          {/* Explanation Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Explanation
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <RichTextEditor
              value={wordFormData.explanation}
              placeholder="Additional explanation or notes"
              onChange={(value) =>
                setWordFormData((prev) => ({
                  ...prev,
                  explanation: value,
                }))
              }
            />
          </Grid>

          {/* Example Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Example
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <RichTextEditor
              value={wordFormData.example}
              placeholder="Provide an example sentence using this word"
              onChange={(value) =>
                setWordFormData((prev) => ({
                  ...prev,
                  example: value,
                }))
              }
            />
          </Grid>

          {/* Media Files Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Media Files
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FileUploadButton
              name="image"
              label="Upload Image"
              accept="image/*"
              onFileSelect={handleImageChange}
              currentFile={wordFormData.image}
              previewUrl={editingWord?.imageUrl}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FileUploadButton
              name="audio"
              label="Upload Audio"
              accept="audio/*"
              onFileSelect={handleAudioChange}
              currentFile={wordFormData.audio}
              previewUrl={editingWord?.audioUrl}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isCreatingWord || isUpdatingWord}
        >
          {editingWord ? "Update" : "Add"} Word
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WordFormDialog;
